/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSignal } from '@preact/signals-react';
import { creativesActions, generalActions } from 'store/actions';
import { compareDateObjects } from 'utils';
import {
  ManageCampaignsHeader,
  FilterBar,
  CampaignDisplayList,
  CustomTableHeader,
  GenericContentEvents,
  WelcomeManageCampaigns,
  Loader,
  CellRendererSelector,
  ErrorComponent,
  InvalidTrackingLinksModal,
} from 'components';
import { StatisticAndChart } from '../../../components/manageCampaigns/statisticBtnAndChart/StatisticAndChart';
import { useToggleCampaignsFilter } from './useToggleCampaignsFilter';
import { ManageCampaignsContext } from 'contexts';
import { mandatoryColumns, allAdditionalFields, flexTableHeader } from 'constants';
import { useAPI } from './useAPI';
import styled from 'styled-components';
import { Palette } from 'config';
import { isPublisherRole } from 'store/selectors';

const doChangesRequest = (data, dateForRequest, newDateForRequest) => {
  //console.log({ data, dateForRequest, newDateForRequest });
  if (!data) return true;
  if (!compareDateObjects(newDateForRequest.current, dateForRequest)) {
    return true;
  }
  return false;
};

const caseInsensitiveComparator = (valueA, valueB) => {
  if (valueA == null) return -1;
  if (valueB == null) return 1;

  return valueA.toString().toLowerCase().localeCompare(valueB.toString().toLowerCase());
};

const defaultSelectedCols = (
  mandatoryColumns,
  allAdditionalFields,
  selectedFields = [],
  events = [],
  eventsCohort = [],
) => {
  let visibleColumns = [];
  selectedFields.forEach((colName) => {
    const visibleCol = allAdditionalFields.find(({ colId }) => colId === colName);
    if (visibleCol) {
      visibleColumns.push({ ...visibleCol, hide: false });
    }
  });

  let hiddenColumns = allAdditionalFields.filter(({ colId }) => !selectedFields.includes(colId));
  const stringFields = ['campaign', 'countries'];
  const defaultCols = [...mandatoryColumns, ...visibleColumns, ...hiddenColumns].map((field) => {
    const isStringField = stringFields.includes(field.field);
    if (!field.lockPosition && !selectedFields.includes(field.colId)) {
      return {
        ...field,
        hide: true,
        headerComponent: CustomTableHeader,
        ...(isStringField ? { comparator: caseInsensitiveComparator } : {}),
      };
    }
    return {
      ...field,
      headerComponent: CustomTableHeader,
      ...(isStringField ? { comparator: caseInsensitiveComparator } : {}),
    };
  });
  const eventsWithCustomHeader = events.length
    ? [...events].map((event) => {
        return {
          ...event,
          headerComponent: CustomTableHeader,
          cellRenderer: GenericContentEvents,
          cellRendererParams: {
            valueType: 'number',
          },
          cellRendererSelector: CellRendererSelector,
        };
      })
    : [];
  const eventsCohortWithCustomHeader = eventsCohort.length
    ? [...eventsCohort].map((event) => {
        return {
          ...event,
          headerComponent: CustomTableHeader,
          cellRenderer: GenericContentEvents,
          cellRendererParams: {
            valueType: 'number',
          },
          cellRendererSelector: CellRendererSelector,
        };
      })
    : [];

  const defaultEvents = eventsWithCustomHeader.length
    ? [{ headerName: 'Events', children: [...eventsWithCustomHeader] }]
    : [{ flexTableHeader }];
  const defaultEventsCohort = eventsCohortWithCustomHeader.length
    ? [
        {
          headerName: 'Events (Cohort)',
          children: [...eventsCohortWithCustomHeader, flexTableHeader],
        },
      ]
    : [{ flexTableHeader }];

  const res = [...defaultCols]
    .concat(
      events.length && eventsCohort.length
        ? [...defaultEvents, ...defaultEventsCohort]
        : [flexTableHeader],
    )
    .concat(events.length === 0 ? [flexTableHeader] : []);
  return res;
};

const getFilteredRowData = (
  rowData,
  statusFilter,
  typeFilter,
  appTypeFilter,
  countriesFilter,
  filteredCamp,
) => {
  let newRowData = structuredClone(rowData);
  if (filteredCamp.length) {
    newRowData = rowData.filter((campData) => {
      return filteredCamp.includes(campData._campaignid);
    });
  }
  if (statusFilter === 'allButArchived') {
    newRowData = newRowData.filter(({ _status_type }) => _status_type !== 9);
  }
  if (statusFilter === 'active') {
    newRowData = newRowData.filter(({ _status_type }) => _status_type === 0);
  }
  if (statusFilter === 'paused') {
    newRowData = newRowData.filter(({ _status_type }) => _status_type === 3);
  }
  if (statusFilter === 'suspended') {
    newRowData = newRowData.filter(({ _status_type }) => _status_type === 8);
  }
  if (typeFilter === 'onBoarding') {
    newRowData = newRowData.filter(({ isOnBoarding }) => isOnBoarding);
  }
  if (typeFilter === 'onGoing') {
    newRowData = newRowData.filter(({ isOnBoarding }) => !isOnBoarding);
  }
  if (appTypeFilter === 'app') {
    newRowData = newRowData.filter(({ isWebsite }) => !isWebsite);
  }
  if (appTypeFilter === 'website') {
    newRowData = newRowData.filter(({ isWebsite }) => isWebsite);
  }
  if (countriesFilter.length) {
    newRowData = newRowData.filter(({ countries }) => {
      const countriesArr = countries.split(',');
      const hasCountry = countriesArr.some((item) => countriesFilter.includes(item));
      return hasCountry;
    });
  }
  return newRowData;
};

export const CampaignsManager = () => {
  const timePeriod = useSelector((state) => state.manageCampaigns.selectedDateRange);
  const dispatch = useDispatch();
  const isPublisher = useSelector(isPublisherRole);
  const trackingModalUrl = useSelector((state) => state.manageCampaigns.trackingModalUrl);

  const {
    tableData,
    isFetchingTableData,
    isFirstLoadingTableData,
    isFirstLoadingDataChartEvents,
    isError,
    openTrackingModal,
  } = useAPI();

  // if (window.REACT_APP_ENV === 'production') {
  //   const newUrl = redirectToOldPlatformUrl(`/Discovery/Manage.aspx`);
  //   console.log(newUrl);
  //   window.location.assign(newUrl);
  //   // window.location.href = newUrl;
  //   return <Loader.Global delay={0} />;
  // }

  const campaignData$ = useSignal(null);

  const [rowData, setRowData] = useState(null);
  const [colDefs, setColDefs] = useState([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const {
    isActiveCamp,
    isActiveApp,
    state: filteredCamp,
    toggleCamp,
    toggleApp,
    handleDeselectCampaigns,
    selectedAppsAndCampaigns,
    appNumberToCampaignNumbers,
    filterByStatus,
    selectedStatusFilter$,
    handleClickOutsideDropDown,
    searchValue$,
    showDropDown$,
    deleteSelectedItem,
    deleteSelectedStatusFilter,
    selectedTypeFilter$,
    filterByType,
    deleteSelectedTypeFilter,
    selectedAppTypeFilter$,
    filterByAppType,
    deleteSelectedAppTypeFilter,
    fullNameCountries,
    selectedCountriesFilter$,
    handleApplyClick,
    all_countries,
    handleDeselectCountry,
    clearAll,
    types,
    statuses,
    appTypes,
    appNumberToAppName,
    campNumberToCampName,
    checkLocalStorageSelectedItems,
    updateTableData,
    gridRef,
    currentFilter$,
  } = useToggleCampaignsFilter(campaignData$, rowData, setRowData, tableData);

  useEffect(() => {
    if (!isFetchingTableData && tableData) {
      const newRowData = getFilteredRowData(
        tableData.campaignsData,
        selectedStatusFilter$.value,
        selectedTypeFilter$.value,
        selectedAppTypeFilter$.value,
        selectedCountriesFilter$.value,
        filteredCamp,
      );
      campaignData$.value = {
        ...tableData,
        campaignsData: newRowData,
        // campaignsData: tableData.campaignsData.filter(({ _status_type }) => _status_type !== 9),
      };
      setRowData(newRowData);
      // setRowData(tableData.campaignsData.filter(({ _status_type }) => _status_type !== 9));
      setColDefs(
        defaultSelectedCols(
          mandatoryColumns,
          allAdditionalFields,
          tableData.selectedColsArray,
          tableData.events,
          tableData.eventsCohort,
        ),
      );
    }
  }, [
    isFetchingTableData,
    tableData,
    // selectedStatusFilter$.value,
    // selectedTypeFilter$.value,
    // selectedCountriesFilter$.value,
  ]);

  useEffect(() => {
    dispatch(creativesActions.resetTrackingAndCreative());
    dispatch(generalActions.resetMainCampaignObject());
  }, []);

  // useEffect(() => {
  // const handleWheel = (e) => {
  //   if (e.target.closest('.modal_base, .dropdown_base')) {
  //     return;
  //   }
  //   e.preventDefault();
  //   const step = 50;
  //   const direction = e.deltaY > 0 ? 1 : -1;
  //   document.documentElement.scrollTop += step * direction;
  // };
  // document.body.addEventListener('wheel', handleWheel, { passive: false });
  // return () => {
  //   document.body.removeEventListener('wheel', handleWheel, { passive: false });
  // };
  // }, []);

  if (isError) {
    return <ErrorComponent />;
  }

  if (isFirstLoadingTableData || isFirstLoadingDataChartEvents) {
    return <Loader.Global delay={0} />;
  }

  if (!tableData?.campaignsData.length && !isFetchingTableData) {
    return (
      <div style={{ marginTop: '120px' }}>
        <Title>Manage Campaigns</Title>
        <WelcomeManageCampaigns />
      </div>
    );
  }

  // if (isFirstLoadingTableData || !rowData) {
  //   return (
  //     <div style={{ height: '723px' }}>
  //       <Loader.Global delay={0} />
  //     </div>
  //   );
  // }

  return (
    <ManageCampaignsContext.Provider
      value={{
        rowData,
        setRowData,
        campaignData$,
        filteredCamp,
        toggleCamp,
        toggleApp,
        isActiveCamp,
        isActiveApp,
        selectedAppsAndCampaigns,
        appNumberToCampaignNumbers,
        handleDeselectCampaigns,
        filterByStatus,
        selectedStatusFilter$,
        handleClickOutsideDropDown,
        searchValue$,
        showDropDown$,
        deleteSelectedItem,
        deleteSelectedStatusFilter,
        selectedTypeFilter$,
        filterByType,
        deleteSelectedTypeFilter,
        selectedAppTypeFilter$,
        filterByAppType,
        deleteSelectedAppTypeFilter,
        fullNameCountries,
        selectedCountriesFilter$,
        handleApplyClick,
        all_countries,
        handleDeselectCountry,
        clearAll,
        types,
        statuses,
        appTypes,
        appNumberToAppName,
        campNumberToCampName,
        checkLocalStorageSelectedItems,
        gridRef,
        colDefs,
        setColDefs,
        updateTableData,
        currentFilter$,
        cpi_campaign_total_cost: tableData?.cpi_campaign_total_cost,
        cpi_campaign_total_installs: tableData?.cpi_campaign_total_installs,
        originalTableData: tableData?.campaignsData,
        openTrackingModal,
        setIsStatusLoading,
      }}
    >
      <div style={{ width: '100%', minWidth: '1200px' }} id='main-page'>
        {isStatusLoading && <Loader.Global delay={0} />}
        {trackingModalUrl && <InvalidTrackingLinksModal />}
        <ManageCampaignsHeader
          isDisabledShowChartBtn={!rowData?.length || !window.IS_CHART_ACTIVE}
          timePeriod={timePeriod}
        />
        {window.IS_CHART_ACTIVE && <StatisticAndChart timePeriod={timePeriod} />}
        <div style={{ minHeight: '675px', marginBottom: '100px' }}>
          <LoaderBox style={{ display: !isFetchingTableData ? 'none' : 'flex' }}>
            <Loader.Local delay={0} />
          </LoaderBox>
          <div style={{ display: isFetchingTableData ? 'none' : 'block' }}>
            <FilterBar />
            <CampaignDisplayList />
          </div>
        </div>
      </div>
    </ManageCampaignsContext.Provider>
  );
};

const Title = styled.h2`
  color: ${Palette.PrimaryGray._900};
  font-size: 40px;
  font-style: normal;
  font-weight: 700;
  line-height: 48px;
  letter-spacing: -0.8px;
`;

const LoaderBox = styled.div`
  height: 675px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`;
